import React from 'react';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface PrimaryButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
  variant?: 'primary' | 'secondary' | 'ghost';
  className?: string;
}

export const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  children,
  onClick,
  disabled = false,
  loading = false,
  fullWidth = true,
  variant = 'primary',
  className,
  ...props
}) => {
  const variants: Record<string, string> = {
    primary: cn(
      'bg-indigo-600 hover:bg-indigo-700 text-white',
      'shadow-lg shadow-indigo-500/20 hover:shadow-indigo-500/30',
      'disabled:bg-slate-300 dark:disabled:bg-slate-700',
      'disabled:text-slate-500 dark:disabled:text-slate-400',
      'disabled:shadow-none'
    ),
    secondary: cn(
      'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200',
      'border border-slate-200 dark:border-slate-700',
      'hover:bg-slate-50 dark:hover:bg-slate-700',
      'disabled:bg-slate-100 dark:disabled:bg-slate-800',
      'disabled:text-slate-400 dark:disabled:text-slate-500'
    ),
    ghost: cn(
      'bg-transparent text-indigo-600 dark:text-indigo-400',
      'hover:bg-indigo-50 dark:hover:bg-indigo-950',
      'disabled:text-slate-400 dark:disabled:text-slate-500'
    ),
  };

  return (
    <button
      data-testid="primary-button"
      onClick={onClick}
      disabled={disabled || loading}
      className={cn(
        'h-11 px-6 rounded-lg font-medium text-sm',
        'flex items-center justify-center gap-2',
        'transition-all duration-150',
        'disabled:cursor-not-allowed',
        'focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:ring-offset-2',
        fullWidth && 'w-full',
        variants[variant],
        className
      )}
      {...props}
    >
      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
      {children}
    </button>
  );
};
